import type { ReactNode } from 'react';
import { useCanGoBack, useRouter, useRouterState } from '@tanstack/react-router';
import { backLinkClasses, Icon } from '@evogard/ui';

declare module '@tanstack/react-router' {
  interface HistoryState {
    /**
     * O nome da tela de onde se veio, para o `BackButton` dizer para onde volta
     * — "Relatório da premiação" em vez de "Voltar". Quem linka é quem sabe.
     */
    backLabel?: string;
  }
}

/**
 * O VOLTAR que volta para onde se veio (28/10/2026, pedido do dono).
 *
 * O "voltar" das telas apontava para um lugar fixo: da ficha do veículo, a
 * agenda — mesmo quando se tinha chegado pelo relatório de uma premiação, e aí
 * voltar era refazer o caminho inteiro pelo Início. Este volta no HISTÓRICO,
 * com o mês, o lado e o filtro que a tela anterior tinha.
 *
 * ## Sem página anterior, o destino de sempre
 *
 * Aberta por um link colado ou numa aba nova, a tela não tem "de onde veio", e
 * voltar no histórico sairia do sistema. Aí o botão vira o `fallback` — o
 * link fixo que a tela já tinha. Ele nunca fica sem ação.
 *
 * O rótulo vem do link de origem (`state.backLabel`); sem ele, "Voltar". O
 * histórico não sabe o nome da página anterior, e inventar um seria a tela
 * afirmando para onde vai sem saber.
 */
export function BackButton({ fallback }: { fallback: ReactNode }) {
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const label = useRouterState({ select: (s) => s.location.state.backLabel });

  if (!canGoBack) return <>{fallback}</>;

  return (
    <button
      type="button"
      onClick={() => router.history.back()}
      className={backLinkClasses()}
    >
      <Icon nome="seta-esquerda" size={16} />
      {label ?? 'Voltar'}
    </button>
  );
}
